import { useState, useEffect, useMemo } from 'react';
import { Search, Filter, Hotel, FileText, Calendar, Mail } from 'lucide-react';
import { motion } from 'framer-motion';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { tourFileAPI, seriesTemplateAPI } from '../services/api';
import { SendToHotelModal } from './SendToHotelModal';

interface BookingsPageProps {
  onSelectBooking: (id: string) => void;
}

export function BookingsPage({ onSelectBooking }: BookingsPageProps) {
  const [hotelSearchQuery, setHotelSearchQuery] = useState('');
  const [selectedHotel, setSelectedHotel] = useState<string>('all');
  const [selectedFileName, setSelectedFileName] = useState<string>('all');
  const [selectedYear, setSelectedYear] = useState<string>('all');
  const [selectedMode, setSelectedMode] = useState<'hotels' | 'vouchers' | null>(null);
  const [selectedBookingGroup, setSelectedBookingGroup] = useState<string>('');
  const [tourFiles, setTourFiles] = useState<any[]>([]);
  const [seriesTemplates, setSeriesTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedTourFile, setSelectedTourFile] = useState<any>(null);

  // Fetch tour files and series templates
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [toursResponse, templatesResponse] = await Promise.all([
          tourFileAPI.getAll(),
          seriesTemplateAPI.getAll()
        ]);
        
        if (toursResponse.success) {
          setTourFiles(toursResponse.data);
        }
        
        if (templatesResponse.success) {
          setSeriesTemplates(templatesResponse.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Extract all unique hotels from tour files
  const allHotels = useMemo(() => {
    const hotelSet = new Set<string>();
    tourFiles.forEach(tour => {
      try {
        const itinerary = typeof tour.itinerary === 'string' ? JSON.parse(tour.itinerary) : (tour.itinerary || []);
        itinerary.forEach((row: any) => {
          if (row.hotelName && row.hotelName.trim()) {
            hotelSet.add(row.hotelName.trim());
          }
        });
      } catch (e) {
        // Ignore parse errors
      }
    });
    return Array.from(hotelSet).sort();
  }, [tourFiles]);

  // Extract all unique file names and years
  const fileNamesAndYears = useMemo(() => {
    const fileNames = new Set<string>();
    const years = new Set<string>();
    tourFiles.forEach(tour => {
      if (tour.tourName && tour.tourName.trim()) {
        fileNames.add(tour.tourName.trim());
      }
      if (tour.year) {
        years.add(String(tour.year));
      }
    });
    return {
      fileNames: Array.from(fileNames).sort(),
      years: Array.from(years).sort((a, b) => parseInt(b) - parseInt(a))
    };
  }, [tourFiles]);

  const filteredHotelOptions = useMemo(() => {
    const query = hotelSearchQuery.trim().toLowerCase();
    if (!query) return allHotels;
    return allHotels.filter((hotel) => hotel.toLowerCase().includes(query));
  }, [allHotels, hotelSearchQuery]);


  const parseDisplayDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    const parts = dateStr.trim().split('-');
    if (parts.length !== 3) return null;

    const day = parseInt(parts[0]);
    const monthStr = parts[1];
    const year = parseInt('20' + parts[2]);

    const monthMap: { [key: string]: number } = {
      Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
      Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11
    };

    const month = monthMap[monthStr];
    if (month === undefined || isNaN(day) || isNaN(year)) return null;

    return new Date(year, month, day);
  };

  const formatDisplayDate = (date: Date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = String(date.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
  };

  const addOneDayToDisplayDate = (dateStr: string) => {
    const parsed = parseDisplayDate(dateStr);
    if (!parsed) return dateStr;
    const next = new Date(parsed);
    next.setDate(next.getDate() + 1);
    return formatDisplayDate(next);
  };

  const areConsecutiveDates = (date1Str: string, date2Str: string) => {
    const date1 = parseDisplayDate(date1Str);
    const date2 = parseDisplayDate(date2Str);
    if (!date1 || !date2) return false;
    const nextDay = new Date(date1);
    nextDay.setDate(nextDay.getDate() + 1);
    return date2.getTime() === nextDay.getTime();
  };

  // Filter bookings by selected hotel, file name, and year
  const filteredBookings = useMemo(() => {
    return tourFiles.filter(tour => {
      // File Name filter
      if (selectedFileName !== 'all' && tour.tourName !== selectedFileName) {
        return false;
      }

      // Year filter
      if (selectedYear !== 'all' && String(tour.year) !== selectedYear) {
        return false;
      }

      // Hotel filter
      if (selectedHotel === 'all') return true;

      try {
        const itinerary = typeof tour.itinerary === 'string' ? JSON.parse(tour.itinerary) : (tour.itinerary || []);
        return itinerary.some((row: any) => row.hotelName?.trim() === selectedHotel);
      } catch (e) {
        return false;
      }
    });
  }, [tourFiles, selectedHotel, selectedFileName, selectedYear]);

  const selectedHotelExtensions = useMemo(() => {
    if (selectedHotel === 'all') return [];
    const normalizedHotel = selectedHotel.trim().toLowerCase();

    const extensions: any[] = [];
    filteredBookings.forEach((tour) => {
      try {
        const rawExtensions = typeof tour.extensions === 'string'
          ? JSON.parse(tour.extensions)
          : (tour.extensions || []);

        if (!Array.isArray(rawExtensions)) return;

        rawExtensions.forEach((extension: any) => {
          const extHotel = String(extension?.hotelName || extension?.hotel || '').trim().toLowerCase();
          if (!extHotel) return;
          if (extHotel === normalizedHotel || extHotel.includes(normalizedHotel) || normalizedHotel.includes(extHotel)) {
            extensions.push(extension);
          }
        });
      } catch (e) {
        // Ignore parse errors
      }
    });

    return extensions;
  }, [filteredBookings, selectedHotel]);

  // Get hotel bookings data for selected hotel
  const hotelBookingsData = useMemo(() => {
    if (selectedHotel === 'all') return [];

    const rows = filteredBookings.flatMap(tour => {
      try {
        const itinerary = typeof tour.itinerary === 'string' ? JSON.parse(tour.itinerary) : (tour.itinerary || []);
        const hotelRows = itinerary
          .filter((row: any) => row.hotelName?.trim() === selectedHotel)
          .sort((a: any, b: any) => {
            const aDate = parseDisplayDate(a.date)?.getTime() ?? Number.POSITIVE_INFINITY;
            const bDate = parseDisplayDate(b.date)?.getTime() ?? Number.POSITIVE_INFINITY;
            return aDate - bDate;
          });
        
        // Generate Group Name: SeriesCode + ArrivalDate (e.g., DIH15Feb26)
        let groupName = tour.fileCode || '';
        if (tour.tourType === 'SERIES' && tour.startDate && tour.tourName) {
          const normalizeSeriesName = (value: string) =>
            String(value || '').toLowerCase().trim().replace(/\s+/g, ' ');

          const normalizedTourName = normalizeSeriesName(tour.tourName);

          const exactTemplate = seriesTemplates.find((t) =>
            normalizeSeriesName(t.seriesName) === normalizedTourName
          );

          let template = exactTemplate;

          if (!template) {
            const candidates = seriesTemplates.filter((t) => {
              const normalizedSeriesName = normalizeSeriesName(t.seriesName);
              return normalizedSeriesName && (
                normalizedTourName.includes(normalizedSeriesName) ||
                normalizedSeriesName.includes(normalizedTourName)
              );
            });

            if (candidates.length > 0) {
              candidates.sort((a, b) =>
                normalizeSeriesName(b.seriesName).length - normalizeSeriesName(a.seriesName).length
              );
              template = candidates[0];
            }
          }

          if (template && template.seriesCode) {
            const seriesCode = template.seriesCode;

            // Format arrival date as ddMmmYY (e.g., 15Feb26)
            const startDate = new Date(tour.startDate);
            const day = String(startDate.getDate()).padStart(2, '0');
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const month = monthNames[startDate.getMonth()];
            const year = String(startDate.getFullYear()).slice(-2);
            const formattedDate = `${day}${month}${year}`;

            groupName = `${seriesCode}${formattedDate}`;
          }
        }
        
        const groups: any[][] = [];
        hotelRows.forEach((row: any) => {
          if (groups.length === 0) {
            groups.push([row]);
            return;
          }
          const lastGroup = groups[groups.length - 1];
          const lastRow = lastGroup[lastGroup.length - 1];
          if (areConsecutiveDates(lastRow.date, row.date)) {
            lastGroup.push(row);
          } else {
            groups.push([row]);
          }
        });

        return groups.map((group: any[]) => {
          const checkInDate = group[0]?.date || '';
          const lastDate = group[group.length - 1]?.date || '';
          const checkOutDate = addOneDayToDisplayDate(lastDate);
          const roomNights = group.length;

          return {
            groupName: groupName,
            fileName: tour.tourName,
            checkIn: checkInDate,
            checkOut: checkOutDate,
            roomNights: roomNights,
            rooms: 12,
            status: '',
            tourFile: tour
          };
        });
      } catch (e) {
        return [];
      }
    }).filter(Boolean);

    const unique = new Map<string, any>();
    rows.forEach((row: any) => {
      const key = [
        row.groupName,
        row.fileName,
        row.checkIn,
        row.checkOut,
        row.roomNights,
        row.rooms
      ].join('|');
      if (!unique.has(key)) unique.set(key, row);
    });

    return Array.from(unique.values());
  }, [filteredBookings, selectedHotel, seriesTemplates]);

  const getfileStatusColor = (fileStatus: string) => {
    switch (fileStatus) {
      case 'completed': return 'bg-green-100 text-green-700 border-green-300';
      case 'in-progress': return 'bg-amber-100 text-amber-700 border-amber-300';
      case 'upcoming': return 'bg-blue-100 text-blue-700 border-blue-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading bookings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Bookings</h1>
          <p className="text-gray-600 mt-1">{filteredBookings.length} bookings found</p>
        </div>
      </div>

      {/* Mode Selection - Hotels or Vouchers */}
      {!selectedMode ? (
        <div className="grid grid-cols-2 gap-6 max-w-2xl mx-auto mt-12">
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setSelectedMode('hotels')}
            className="p-8 bg-white rounded-lg shadow-md border-2 border-gray-200 hover:border-violet-500 cursor-pointer transition-all"
          >
            <div className="text-center">
              <Hotel className="w-16 h-16 text-violet-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Hotels</h3>
              <p className="text-gray-500">View and manage hotel bookings</p>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setSelectedMode('vouchers')}
            className="p-8 bg-white rounded-lg shadow-md border-2 border-gray-200 hover:border-violet-500 cursor-pointer transition-all"
          >
            <div className="text-center">
              <FileText className="w-16 h-16 text-violet-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Vouchers</h3>
              <p className="text-gray-500">View and manage vouchers</p>
            </div>
          </motion.div>
        </div>
      ) : (
        <>
          {/* Back Button */}
          <Button 
            onClick={() => { setSelectedMode(null); setSelectedHotel('all'); }} 
            variant="outline"
            className="mb-4"
          >
            ← Back to Options
          </Button>

          {/* Search and Filters */}
          <div className="flex justify-end">
            <div className="w-full rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
              <div className="flex items-center gap-4 border-b border-gray-100 bg-gray-50 px-4 py-3">
                <Filter className="w-4 h-4 text-violet-600" />
                
                {/* File Name Filter */}
                <select
                  value={selectedFileName}
                  onChange={(e) => setSelectedFileName(e.target.value)}
                  className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                >
                  <option value="all">All Files</option>
                  {fileNamesAndYears.fileNames.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>

                {/* Year Filter */}
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                >
                  <option value="all">All Years</option>
                  {fileNamesAndYears.years.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>

                {/* Hotel Search */}
                <Input
                  value={hotelSearchQuery}
                  onChange={(e) => setHotelSearchQuery(e.target.value)}
                  placeholder={`Search ${selectedMode === 'hotels' ? 'hotel' : 'voucher'} names...`}
                  className="border-0 bg-transparent p-0 shadow-none focus-visible:ring-0"
                />
              </div>
              {hotelSearchQuery.trim() ? (
                <div className="max-h-56 overflow-y-auto p-2">
                  {filteredHotelOptions.length > 0 ? (
                    filteredHotelOptions.map((hotel) => (
                      <button
                        key={hotel}
                        type="button"
                        onClick={() => {
                          setSelectedHotel(hotel);
                          setHotelSearchQuery('');
                        }}
                        className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm transition-colors ${
                          selectedHotel === hotel
                            ? 'bg-violet-50 text-violet-700'
                            : 'hover:bg-gray-50 text-gray-700'
                        }`}
                      >
                        <span className="truncate">{hotel}</span>
                        {selectedHotel === hotel && <span className="text-violet-600 font-semibold">Selected</span>}
                      </button>
                    ))
                  ) : (
                    <div className="px-3 py-4 text-sm text-gray-500 text-center">
                      No hotel names match your search.
                    </div>
                  )}
                </div>
              ) : (
                <div className="px-4 py-4 text-sm text-gray-500">
                  ...
                </div>
              )}
            </div>
          </div>

          {/* Bookings Table */}
          {selectedHotel !== 'all' ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 bg-violet-50 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {selectedMode === 'hotels' ? <Hotel className="w-5 h-5 text-violet-600" /> : <FileText className="w-5 h-5 text-violet-600" />}
                  <h2 className="text-lg font-semibold text-gray-900">
                    {selectedMode === 'hotels' ? 'Hotel' : 'Voucher'}: {selectedHotel}
                  </h2>
                </div>
                {selectedMode === 'hotels' && (
                  <Button
                    onClick={() => {
                      setSelectedBookingGroup('');
                      setShowSendModal(true);
                    }}
                    className="bg-violet-600 hover:bg-violet-700 text-white flex items-center gap-2"
                    size="sm"
                  >
                    <Mail className="w-4 h-4" />
                    Send to Hotel
                  </Button>
                )}
              </div>
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Group Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">File Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check-in</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check-out</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Room Nights</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rooms</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {hotelBookingsData.map((booking: any, idx: number) => (
                    <motion.tr
                      key={idx}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        {selectedMode === 'vouchers' ? (
                          <span 
                            className="font-medium text-violet-600 cursor-pointer hover:text-violet-800 hover:underline"
                            onClick={() => {
                              setSelectedBookingGroup(booking.groupName);
                              setSelectedTourFile(booking.tourFile || null);
                              setShowSendModal(true);
                            }}
                          >
                            {booking.groupName}
                          </span>
                        ) : (
                          <span className="font-medium text-violet-600">
                            {booking.groupName}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium">{booking.fileName}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          {booking.checkIn}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          {booking.checkOut}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-semibold text-blue-600">{booking.roomNights}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-semibold">{booking.rooms}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {booking.status && (
                          <Badge className="bg-blue-100 text-blue-700 border border-blue-300">
                            {booking.status}
                          </Badge>
                        )}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              {selectedMode === 'hotels' ? <Hotel className="w-16 h-16 text-gray-300 mx-auto mb-4" /> : <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />}
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select a {selectedMode === 'hotels' ? 'Hotel' : 'Voucher'}</h3>
              <p className="text-gray-500">Choose a {selectedMode === 'hotels' ? 'hotel' : 'voucher'} from the dropdown above to view bookings</p>
            </div>
          )}
        </>
      )}

      {/* Send to Hotel/Voucher Modal */}
      <SendToHotelModal
        isOpen={showSendModal}
        onClose={() => {
          setShowSendModal(false);
          setSelectedBookingGroup('');
          setSelectedTourFile(null);
        }}
        hotelName={selectedHotel}
        bookings={hotelBookingsData.map((b: any) => ({
          groupName: b.groupName,
          fileName: b.fileName,
          checkIn: b.checkIn,
          checkOut: b.checkOut,
          nights: b.roomNights,
          rooms: b.rooms
        }))}
        mode={selectedMode || 'hotels'}
        groupName={selectedBookingGroup}
        tourFileData={selectedTourFile}
        selectedHotelName={selectedHotel}
        extensions={selectedHotelExtensions}
      />
    </div>
  );
}
